import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatPhoneNumber, cleanPhoneNumber } from '../utils/formatters';
import { getApiUrl, getRecaptchaSiteKey, ApiConfigurationError } from '../utils/apiUtils';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const recaptchaSiteKey = useMemo(() => getRecaptchaSiteKey(), []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Auto-format phone number
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Check if reCAPTCHA is enabled and verify token
    if (recaptchaSiteKey && !recaptchaToken) {
      setSubmitStatus('recaptcha_error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Clean data for backend (remove formatting)
      const cleanedData = {
        ...formData,
        phone: cleanPhoneNumber(formData.phone),
        recaptchaToken: recaptchaToken
      };

      const response = await fetch(getApiUrl('contact.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
      } else {
        setSubmitStatus('error');
        // Reset reCAPTCHA on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      if (error instanceof ApiConfigurationError) {
        setSubmitStatus('config_error');
      } else {
        setSubmitStatus('error');
      }
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('contact.title')} | Tap'nGrab</title>
        <meta name="description" content={t('contact.subtitle')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t('contact.title')} | Tap'nGrab`} />
        <meta property="og:description" content={t('contact.subtitle')} />
        <meta property="og:url" content={`https://tapngrab.ca/${i18n.language === 'fr' ? 'fr/' : ''}contact`} />
        <meta property="og:image" content="https://tapngrab.ca/images/smart-store.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t('contact.title')} | Tap'nGrab`} />
        <meta name="twitter:description" content={t('contact.subtitle')} />
        <meta name="twitter:image" content="https://tapngrab.ca/images/smart-store.png" />
        <link rel="canonical" href="https://tapngrab.ca/contact" />
        <link rel="alternate" hreflang="fr" href="https://tapngrab.ca/fr/contact" />
        <link rel="alternate" hreflang="en" href="https://tapngrab.ca/contact" />
      </Helmet>

      <div className="text-slate-800 bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="hero-grad py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl">
                {t('contact.title')}
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
                {t('contact.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border bg-white p-8 chip">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.subject')} *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                    >
                      <option value="">Select subject</option>
                      {Object.entries(t('contact.form.subject_options', { returnObjects: true })).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                  />
                </div>

                {/* reCAPTCHA */}
                {recaptchaSiteKey && (
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteKey}
                      onChange={handleRecaptchaChange}
                      theme="light"
                    />
                  </div>
                )}

                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-2xl px-6 py-3 text-sm font-semibold text-white glow disabled:opacity-50"
                    style={{ background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #6366f1)' }}
                  >
                    {isSubmitting ? 'Sending...' : t('contact.form.submit')}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-sm text-green-800">{t('contact.form.success')}</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm text-red-800">{t('contact.form.error')}</p>
                  </div>
                )}

                {submitStatus === 'config_error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm text-red-800">
                      Configuration error: API URL is not properly configured. Please contact support.
                    </p>
                  </div>
                )}

                {submitStatus === 'recaptcha_error' && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <p className="text-sm text-yellow-800">
                      {t('contact.form.recaptcha_error_message')}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="bg-slate-50 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                Get in Touch
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                We're here to help with any questions about Tap'nGrab smart coolers.
              </p>
            </div>
            
            <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-3">
              <div className="text-center">
                <div className="text-4xl mb-4">📧</div>
                <h3 className="text-lg font-semibold text-slate-900">Email</h3>
                <p className="mt-2 text-sm text-slate-600">info@tapngrab.ca</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-4">📍</div>
                <h3 className="text-lg font-semibold text-slate-900">Service Area</h3>
                <p className="mt-2 text-sm text-slate-600">Montreal & South Shore, Quebec</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-4">⏰</div>
                <h3 className="text-lg font-semibold text-slate-900">Response Time</h3>
                <p className="mt-2 text-sm text-slate-600">Within 48 hours</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Contact;
